type SpanPerson = {id: string; start: Date; end: Date; name: string};

export type PtoThemeMode = 'light' | 'dark';

type RampAnchor = {h: number; s: number; l: number};

/** Fixed ramp length — overlap layers beyond the last step reuse the darkest fill. */
export const PTO_RAMP_STEPS = 5;

/** Gray → slate anchors per theme (keeps overlap density visible without looking selected). */
const PTO_RAMP_ANCHORS: Record<PtoThemeMode, {start: RampAnchor; end: RampAnchor}> = {
  light: {
    start: {h: 220, s: 6, l: 93},
    end: {h: 218, s: 24, l: 66},
  },
  dark: {
    start: {h: 220, s: 14, l: 44},
    end: {h: 218, s: 26, l: 26},
  },
};

const lerp = (from: number, to: number, t: number): number => from + (to - from) * t;

const ptoFillAtStep = (step: number, steps: number, mode: PtoThemeMode): string => {
  const {start, end} = PTO_RAMP_ANCHORS[mode];
  const t = steps <= 1 ? 0 : step / (steps - 1);
  const h = Math.round(lerp(start.h, end.h, t));
  const s = Math.round(lerp(start.s, end.s, t));
  const l = Math.round(lerp(start.l, end.l, t));
  return `hsl(${h} ${s}% ${l}%)`;
};

/** Gray → slate ramp for PTO overlap layers. */
export const buildPtoFillRamp = (mode: PtoThemeMode = 'light'): string[] =>
  Array.from({length: PTO_RAMP_STEPS}, (_, index) => ptoFillAtStep(index, PTO_RAMP_STEPS, mode));

/** Fill for overlap layer index (0 = solo / bottom overlap). */
export const ptoFillForLayer = (layer: number, ramp: string[]): string => {
  const last = ramp.length - 1;
  return ramp[Math.min(Math.max(layer, 0), last)];
};

export const spanMs = (person: {start: Date; end: Date}): number =>
  person.end.getTime() - person.start.getTime();

/** Longest span first — used for overlap layer order and paint stacking. */
export const compareBySpanLength = (
  left: {start: Date; end: Date; name: string},
  right: {start: Date; end: Date; name: string}
): number =>
  spanMs(right) - spanMs(left) ||
  left.start.getTime() - right.start.getTime() ||
  left.name.localeCompare(right.name);

/**
 * Stable calendar lanes for overlapping PTO intervals. Earlier active spans get
 * lower/lighter lanes; later spans stack darker above them. A lane is reused
 * once its previous span ended before the next person's start day.
 */
export const buildPersonOverlapLayers = (days: {people: SpanPerson[]}[]): Map<string, number> => {
  const peopleById = new Map<string, SpanPerson>();
  for (const {people} of days) {
    for (const person of people) {
      peopleById.set(person.id, person);
    }
  }

  const ordered = [...peopleById.values()].sort(
    (left, right) =>
      left.start.getTime() - right.start.getTime() ||
      right.end.getTime() - left.end.getTime() ||
      left.name.localeCompare(right.name)
  );

  const layers = new Map<string, number>();
  const active: Array<{layer: number; end: Date}> = [];

  for (const person of ordered) {
    for (let index = active.length - 1; index >= 0; index -= 1) {
      if (active[index].end.getTime() < person.start.getTime()) {
        active.splice(index, 1);
      }
    }

    const occupiedLayers = new Set(active.map(({layer}) => layer));
    let layer = 0;
    while (occupiedLayers.has(layer)) {
      layer += 1;
    }

    layers.set(person.id, layer);
    active.push({layer, end: person.end});
  }

  return layers;
};
